/**
 * Agora RTC Manager - 真正的 FSM 版本
 *
 * 架构设计：
 * 1. RemoteMediaFSM - 纯状态机（状态迁移逻辑）
 * 2. EffectLayer - 副作用层（监听状态变化执行副作用）
 * 3. AgoraRTCManager - 协调层（SDK 事件 → FSM 事件映射）
 *
 * 核心改进：
 * ✅ 显式状态迁移规则
 * ✅ 状态多维（lifecycle, video, audio, view）
 * ✅ 副作用统一在 EffectLayer 触发
 * ✅ FSM 独立可测试
 * ✅ 无 isPlaying 补丁，用状态驱动
 * ✅ Manager 瘦身（职责单一）
 */

import AgoraRTC from 'agora-rtc-sdk-ng'
import agoraEventBus, { AGORA_EVENTS } from '@/events/agoraEvents'
import { getVideoConfig, getAudioConfig } from './utils'
import { createLogger } from '@/utils/logger'
import { RemoteMediaFSM, FSMEvent } from './RemoteMediaFSM'
import { createEffectLayer } from './EffectLayer'

const appId = import.meta.env.VITE_AGORA_APP_ID
const logger = createLogger('AgoraRTCManager')

export class AgoraRTCManagerFSM {
  // ========== 私有属性 ==========

  #client = null
  #localAudioTrack = null
  #localVideoTrack = null

  // FSM 核心
  #fsms = new Map() // uid -> RemoteMediaFSM
  #trackEventListeners = new Map() // uid -> Map<mediaType, cleanupFns[]>

  // Track 创建/发布 Promise
  #tracksCreatedPromise = null
  #tracksPublishedPromise = null

  #isEventListenerSetup = false

  // ========== 初始化 ==========

  /**
   * 初始化客户端
   */
  initClient() {
    if (this.#client) return

    try {
      this.#client = AgoraRTC.createClient({
        mode: 'live',
        codec: 'vp8',
        role: 'host',
      })
      logger.info('Client created')
      this.#setupEventListeners()
    } catch (error) {
      logger.error('Init client failed:', error)
      throw error
    }
  }

  /**
   * 加入频道
   */
  async joinChannel({ channelId, token, uid }) {
    if (!this.#client) {
      this.initClient()
    }
    try {
      return await this.#client.join(appId, channelId, token, uid)
    } catch (error) {
      logger.error('Join channel failed:', error)
      throw error
    }
  }

  // ========== 本地 Track 管理 ==========

  /**
   * 创建本地轨道
   */
  createTracks(rtcConfig = {}) {
    if (this.#tracksCreatedPromise) {
      return this.#tracksCreatedPromise
    }

    this.#tracksCreatedPromise = this.#createTracks(rtcConfig)
      .catch(error => {
        this.#tracksCreatedPromise = null
        throw error
      })

    return this.#tracksCreatedPromise
  }

  async #createTracks(rtcConfig) {
    logger.log('Creating local tracks with config:', rtcConfig)

    const audioConfig = getAudioConfig(rtcConfig)
    const videoConfig = getVideoConfig(rtcConfig)

    const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
      audioConfig,
      videoConfig
    )

    this.#localAudioTrack = audioTrack
    this.#localVideoTrack = videoTrack

    return { audio: audioTrack, video: videoTrack }
  }

  /**
   * 发布本地轨道
   */
  async publishTracks() {
    if (this.#tracksPublishedPromise) {
      return this.#tracksPublishedPromise
    }

    if (!this.#tracksCreatedPromise) {
      throw new Error('tracks not created yet')
    }

    this.#tracksPublishedPromise = this.#tracksCreatedPromise
      .then(tracks => this.#publishTracks(tracks))
      .catch(error => {
        this.#tracksPublishedPromise = null
        throw error
      })

    return this.#tracksPublishedPromise
  }

  async #publishTracks(tracks) {
    try {
      logger.log('Publishing local tracks...')
      await this.#client.publish([tracks.audio, tracks.video])
      logger.info('Local tracks published ✓')
    } catch (error) {
      logger.error('Publish tracks failed:', error)
      throw error
    }
  }

  /**
   * 播放本地视频
   */
  async playLocal(element) {
    logger.log('Playing local video...', element)
    if (!element || !this.#tracksCreatedPromise) return

    const tracks = await this.#tracksCreatedPromise
    if (tracks.video) {
      tracks.video.play(element, { fit: 'cover' })
    }
  }

  /**
   * 切换本地音频
   */
  async toggleAudio(enabled) {
    if (this.#localAudioTrack) {
      await this.#localAudioTrack.setEnabled(enabled)
      logger.log('Audio toggled:', enabled)
      return enabled
    }
    return false
  }

  /**
   * 切换本地视频
   */
  async toggleVideo(enabled) {
    if (this.#localVideoTrack) {
      await this.#localVideoTrack.setEnabled(enabled)
      // 触发本地视频状态变化事件
      agoraEventBus.emit(AGORA_EVENTS.LOCAL_VIDEO_STATE_CHANGED, { playing: enabled })
      return true
    }
    return false
  }

  /**
   * 切换前后摄像头
   */
  async switchCamera(facingMode) {
    if (!this.#localVideoTrack) {
      return false
    }

    try {
      // 切换到新摄像头
      await this.#localVideoTrack.setDevice({
        facingMode
      })

      return true
    } catch (error) {
      return false
    }
  }

  // ========== FSM 管理 ==========

  /**
   * 获取或创建 FSM
   * @param {number} uid - 用户 ID
   * @returns {RemoteMediaFSM}
   */
  #getOrCreateFSM(uid) {
    if (!this.#fsms.has(uid)) {
      const fsm = new RemoteMediaFSM(uid)

      // 创建副作用层
      const effectHandler = createEffectLayer({
        // 播放视频
        onPlayVideo: (uid, track, element) => {
          try {
            track.play(element, { fit: 'cover' })
            logger.log(`[Effect] Playing video for ${uid}`)
          } catch (error) {
            logger.error(`[Effect] Failed to play video for ${uid}:`, error)
          }
        },

        // 停止视频
        onStopVideo: (uid, track) => {
          try {
            track.stop()
            logger.log(`[Effect] Stopped video for ${uid}`)
          } catch (error) {
            logger.error(`[Effect] Failed to stop video for ${uid}:`, error)
          }
        },

        // 播放音频
        onPlayAudio: (uid, track) => {
          try {
            track.play()
            logger.log(`[Effect] Playing audio for ${uid}`)
          } catch (error) {
            logger.error(`[Effect] Failed to play audio for ${uid}:`, error)
          }
        },

        // 停止音频（预留）
        onStopAudio: (uid) => {
          // 音频通常不需要显式停止
          logger.log(`[Effect] Audio stopped for ${uid}`)
        },

        // 上报首帧
        onReportFirstFrame: (uid, firstFrameTime, mediaType) => {
          agoraEventBus.emit(AGORA_EVENTS.TRACK_FIRST_FRAME_DECODED, {
            uid,
            firstFrameTime,
            mediaType,
          })
          logger.log(`[Effect] Reported first frame for ${uid}: ${firstFrameTime}s`)
        },

        // 发射 EventBus 事件（向后兼容）
        onEventBusEmit: ({ type, payload }) => {
          if (type === 'STATE_TRANSITION') {
            // 发射统一的状态变化事件
            agoraEventBus.emit(AGORA_EVENTS.USER_STATE_CHANGED, payload)
          }
        },
      })

      // 订阅状态变化，执行副作用
      fsm.subscribe(effectHandler)

      // 订阅状态变化，检查是否应该触发播放
      fsm.subscribe((result) => {
        const { nextState } = result

        // 检查是否满足播放条件
        if (fsm.shouldPlayVideo()) {
          // 当 video.published + view.mounted 都满足时
          // 我们需要等待 Track 的 first-frame-decoded 事件
          // 但这里我们先标记为可以播放（实际播放由 EffectLayer 触发）

          // 注意：这里不立即触发 VIDEO_FIRST_FRAME
          // 而是等待 Track 事件回调时触发
          logger.log(`[FSM] Ready to play video for ${nextState.uid}`)
        }
      })

      this.#fsms.set(uid, fsm)
      logger.log(`Created FSM for uid ${uid}`)
    }

    return this.#fsms.get(uid)
  }

  // ========== 组件 API（声明式）==========

  /**
   * 挂载远程视频
   * @param {number} uid - 用户 ID
   * @param {HTMLElement|string} element - DOM 元素或元素 ID
   */
  mountRemoteVideo(uid, element) {
    if (!element) {
      logger.warn(`mountRemoteVideo: element is null for uid ${uid}`)
      return
    }

    // 同步已存在的远程用户（如果该用户已经发布了流）
    this.#syncExistingRemoteUser(uid)

    const fsm = this.#getOrCreateFSM(uid)

    // 触发 VIEW_MOUNTED 事件
    const result = fsm.dispatch({
      type: FSMEvent.VIEW_MOUNTED,
      payload: { element }
    })

    logger.log(`Mounted remote video for uid ${uid}`, {
      success: result.success,
      viewState: result.nextState.view.state,
      videoState: result.nextState.video.state,
    })
  }

  /**
   * 同步已存在的远程用户
   * 当组件挂载时，如果用户已经发布了流，需要同步状态
   * @param {number} uid - 用户 ID
   * @private
   */
  #syncExistingRemoteUser(uid) {
    if (!this.#client) return

    // 获取 SDK 中的远程用户
    const remoteUsers = this.#client.remoteUsers
    const existingUser = remoteUsers.find(user => user.uid === uid)

    if (!existingUser) {
      logger.log(`No existing remote user found for uid ${uid}`)
      return
    }

    const fsm = this.#getOrCreateFSM(uid)
    const state = fsm.getState()

    // 如果 FSM 的 lifecycle 还是 idle，触发 USER_JOINED
    if (state.lifecycle === 'idle') {
      fsm.dispatch({ type: FSMEvent.USER_JOINED })
      logger.log(`Synced USER_JOINED for uid ${uid}`)
    }

    // 如果用户已经发布了视频，但 FSM 状态还是 idle，同步状态
    if (existingUser.videoTrack && state.video.state === 'idle') {
      fsm.dispatch({
        type: FSMEvent.VIDEO_PUBLISHED,
        payload: { track: existingUser.videoTrack }
      })

      // 注意：不需要重新设置 Track 事件监听
      // 因为在 #handleUserPublished 中已经设置过了
      // Track 对象是同一个，重复设置会导致事件被触发多次

      logger.log(`Synced VIDEO_PUBLISHED for uid ${uid}`)
    }

    // 如果用户已经发布了音频，但 FSM 状态还是 idle，同步状态
    if (existingUser.audioTrack && state.audio.state === 'idle') {
      fsm.dispatch({
        type: FSMEvent.AUDIO_PUBLISHED,
        payload: { track: existingUser.audioTrack }
      })

      // 同样，音频也不需要重新设置监听器

      logger.log(`Synced AUDIO_PUBLISHED for uid ${uid}`)
    }
  }

  /**
   * 卸载远程视频
   * @param {number} uid - 用户 ID
   */
  unmountRemoteVideo(uid) {
    const fsm = this.#fsms.get(uid)
    if (!fsm) return

    fsm.dispatch({ type: FSMEvent.VIEW_UNMOUNTED })
    logger.log(`Unmounted remote video for uid ${uid}`)
  }

  /**
   * 获取用户状态（调试用）
   * @param {number} uid - 用户 ID
   */
  getRemoteUserState(uid) {
    const fsm = this.#fsms.get(uid)
    return fsm ? fsm.getState() : null
  }

  /**
   * 获取所有用户状态
   */
  getAllRemoteUserStates() {
    return Array.from(this.#fsms.values()).map(fsm => fsm.getState())
  }

  // ========== SDK 事件处理 ==========

  /**
   * 用户发布流
   */
  async #handleUserPublished(user, mediaType) {
    const { uid } = user

    try {
      logger.log(`User published ${mediaType}: ${uid}`)

      // 订阅
      await this.#client.subscribe(user, mediaType)
      logger.log(`Subscribed to ${uid} ${mediaType}`)

      const fsm = this.#getOrCreateFSM(uid)

      // 首次创建 FSM 时，触发 USER_JOINED 事件
      const state = fsm.getState()
      if (state.lifecycle === 'idle') {
        fsm.dispatch({ type: FSMEvent.USER_JOINED })

        // 发射 EventBus 事件（供外部监听）
        agoraEventBus.emit(AGORA_EVENTS.USER_JOINED, { uid })
        logger.log(`User ${uid} joined, emitted USER_JOINED event`)
      }

      if (mediaType === 'video') {
        // SDK 事件 → FSM 事件
        fsm.dispatch({
          type: FSMEvent.VIDEO_PUBLISHED,
          payload: { track: user.videoTrack }
        })

        // 设置 Track 事件监听
        this.#setupVideoTrackEvents(uid, user.videoTrack)

        // 检查是否应该播放
        if (fsm.shouldPlayVideo()) {
          // 如果 view 已 mounted，触发播放
          // 注意：真正的播放由 first-frame-decoded 触发
          // 这里只是记录日志
          logger.log(`Video ready to play for ${uid}`)
        }
      }

      if (mediaType === 'audio') {
        fsm.dispatch({
          type: FSMEvent.AUDIO_PUBLISHED,
          payload: { track: user.audioTrack }
        })

        this.#setupAudioTrackEvents(uid, user.audioTrack)
      }
    } catch (error) {
      logger.error(`Subscribe failed for ${uid} ${mediaType}:`, error)
    }
  }

  /**
   * 用户取消发布
   */
  async #handleUserUnpublished(user, mediaType) {
    const { uid } = user

    logger.log(`User unpublished ${mediaType}: ${uid}`)

    const fsm = this.#fsms.get(uid)
    if (!fsm) return

    if (mediaType === 'video') {
      this.#cleanupTrackEvents(uid, 'video')
      fsm.dispatch({ type: FSMEvent.VIDEO_UNPUBLISHED })
    }

    if (mediaType === 'audio') {
      this.#cleanupTrackEvents(uid, 'audio')
      fsm.dispatch({ type: FSMEvent.AUDIO_UNPUBLISHED })
    }
  }

  /**
   * 用户离开
   */
  async #handleUserLeft(user, reason) {
    const { uid } = user

    logger.log(`User left: ${uid}, reason: ${reason}`)

    const fsm = this.#fsms.get(uid)
    if (!fsm) return

    // 清理所有 Track 事件
    this.#cleanupTrackEvents(uid, 'all')

    // 触发 FSM 事件
    fsm.dispatch({ type: FSMEvent.USER_LEFT })

    // 发射 EventBus 事件（向后兼容）
    agoraEventBus.emit(AGORA_EVENTS.USER_LEFT, { uid })

    // 清理 FSM
    fsm.destroy()
    this.#fsms.delete(uid)

    logger.log(`Cleaned up FSM for ${uid}`)
  }

  // ========== Track 事件管理 ==========

  /**
   * 设置视频 Track 事件监听
   */
  #setupVideoTrackEvents(uid, videoTrack) {
    const cleanupFns = []
    const publishTime = Date.now()

    // 首帧解码
    const onFirstFrame = () => {
      const firstFrameTime = (Date.now() - publishTime) / 1000
      const fsm = this.#fsms.get(uid)

      if (fsm) {
        // Track 事件 → FSM 事件
        fsm.dispatch({
          type: FSMEvent.VIDEO_FIRST_FRAME,
          payload: { firstFrameTime }
        })
      }

      logger.log(`First frame decoded for ${uid}: ${firstFrameTime.toFixed(3)}s`)
    }

    videoTrack.on('first-frame-decoded', onFirstFrame)
    cleanupFns.push(() => videoTrack.off('first-frame-decoded', onFirstFrame))

    // 视频可见性
    const onVisibleStatus = (data) => {
      agoraEventBus.emit(AGORA_EVENTS.TRACK_VIDEO_VISIBLE, {
        uid,
        visible: data.visible,
      })
      logger.log(`Video ${data.visible ? 'visible' : 'hidden'} for ${uid}`)
    }

    videoTrack.on('video-element-visible-status', onVisibleStatus)
    cleanupFns.push(() => videoTrack.off('video-element-visible-status', onVisibleStatus))

    // Track 结束
    const onTrackEnded = () => {
      const fsm = this.#fsms.get(uid)
      if (fsm) {
        fsm.dispatch({ type: FSMEvent.VIDEO_TRACK_ENDED })
      }

      agoraEventBus.emit(AGORA_EVENTS.TRACK_ENDED, { uid, mediaType: 'video' })
      logger.log(`Video track ended for ${uid}`)
    }

    videoTrack.on('track-ended', onTrackEnded)
    cleanupFns.push(() => videoTrack.off('track-ended', onTrackEnded))

    // 保存清理函数
    if (!this.#trackEventListeners.has(uid)) {
      this.#trackEventListeners.set(uid, new Map())
    }
    this.#trackEventListeners.get(uid).set('video', cleanupFns)

    logger.log(`Setup video track events for ${uid}`)
  }

  /**
   * 设置音频 Track 事件监听
   */
  #setupAudioTrackEvents(uid, audioTrack) {
    const cleanupFns = []

    // Track 结束
    const onTrackEnded = () => {
      const fsm = this.#fsms.get(uid)
      if (fsm) {
        fsm.dispatch({ type: FSMEvent.AUDIO_TRACK_ENDED })
      }

      agoraEventBus.emit(AGORA_EVENTS.TRACK_ENDED, { uid, mediaType: 'audio' })
      logger.log(`Audio track ended for ${uid}`)
    }

    audioTrack.on('track-ended', onTrackEnded)
    cleanupFns.push(() => audioTrack.off('track-ended', onTrackEnded))

    if (!this.#trackEventListeners.has(uid)) {
      this.#trackEventListeners.set(uid, new Map())
    }
    this.#trackEventListeners.get(uid).set('audio', cleanupFns)

    logger.log(`Setup audio track events for ${uid}`)
  }

  /**
   * 清理 Track 事件监听
   */
  #cleanupTrackEvents(uid, mediaType) {
    const userListeners = this.#trackEventListeners.get(uid)
    if (!userListeners) return

    if (mediaType === 'all') {
      userListeners.forEach((cleanupFns, type) => {
        cleanupFns.forEach(fn => fn())
        logger.log(`Cleaned up ${type} track events for ${uid}`)
      })
      this.#trackEventListeners.delete(uid)
    } else {
      const cleanupFns = userListeners.get(mediaType)
      if (cleanupFns) {
        cleanupFns.forEach(fn => fn())
        userListeners.delete(mediaType)
        logger.log(`Cleaned up ${mediaType} track events for ${uid}`)
      }
    }
  }

  // ========== 资源清理 ==========

  /**
   * 销毁并清理所有资源
   */
  async destroy() {
    logger.log('Destroying...')

    // 清理所有 FSM
    this.#fsms.forEach((fsm, uid) => {
      this.#cleanupTrackEvents(uid, 'all')
      fsm.destroy()
    })
    this.#fsms.clear()

    // 关闭本地 tracks
    await this.#closeLocalTracks()

    // 离开频道
    if (this.#client) {
      await this.#client.leave()
    }

    // 清空状态
    this.#trackEventListeners.clear()

    // 重置
    this.#client = null
    this.#tracksCreatedPromise = null
    this.#tracksPublishedPromise = null
    this.#isEventListenerSetup = false

    logger.log('Destroyed successfully')
  }

  async #closeLocalTracks() {
    if (this.#tracksCreatedPromise) {
      try {
        logger.log('Waiting for tracks creation to complete before closing...')
        const tracks = await this.#tracksCreatedPromise

        if (tracks.audio) {
          tracks.audio.close()
        }
        if (tracks.video) {
          tracks.video.stop()
          tracks.video.close()
        }
      } catch (error) {
        logger.log('Tracks creation failed, nothing to close')
      }
    }

    this.#localAudioTrack = null
    this.#localVideoTrack = null
  }

  // ========== 事件监听器设置 ==========

  #setupEventListeners() {
    if (!this.#client || this.#isEventListenerSetup) return

    this.#isEventListenerSetup = true

    // 用户发布流
    this.#client.on('user-published', (user, mediaType) => {
      this.#handleUserPublished(user, mediaType)
    })

    // 用户取消发布
    this.#client.on('user-unpublished', (user, mediaType) => {
      this.#handleUserUnpublished(user, mediaType)
    })

    // 用户离开
    this.#client.on('user-left', (user, reason) => {
      this.#handleUserLeft(user, reason)
    })

    // 连接状态变化
    this.#client.on('connection-state-change', (curState, prevState, reason) => {
      agoraEventBus.emit(AGORA_EVENTS.CONNECTION_STATE_CHANGE, {
        from: prevState,
        to: curState,
        reason,
      })
    })

    // Token 事件
    this.#client.on('token-privilege-will-expire', () => {
      agoraEventBus.emit(AGORA_EVENTS.TOKEN_PRIVILEGE_WILL_EXPIRE)
    })

    this.#client.on('token-privilege-did-expire', () => {
      agoraEventBus.emit(AGORA_EVENTS.TOKEN_PRIVILEGE_DID_EXPIRE)
    })

    logger.log('Event listeners setup complete')
  }
}

// 导出单例（可选，也可以让用户自己创建实例）
export default new AgoraRTCManagerFSM()
